import { useState } from 'react';
import { useTheme } from 'next-themes';
import { PageContainer } from '@/components/common/PageContainer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Settings as SettingsIcon,
  Bell,
  Shield,
  Palette,
  Mail,
  Smartphone,
  Globe,
  Database,
  Key,
  Webhook,
  Save,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/hooks/useAuth';
import { Link } from 'react-router-dom';
import { Upload } from 'lucide-react';

export default function Settings() {
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const { tp, lang } = useI18n();
  const { user } = useAuth();

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast({
        title: tp.settings.saveSuccessTitle,
        description: tp.settings.saveSuccessDescription,
      });
    }, 1000);
  };

  return (
    <PageContainer>
      <div className="space-y-4 md:space-y-6" dir="rtl">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{tp.settings.pageTitle}</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            {tp.settings.pageDescription}
          </p>
        </div>

        {/* Admin-only tools */}
        {user?.role === 'admin' && (
          <Card className="p-4">
            <Link
              to="/dashboard/admin/geo-upload"
              className="flex items-center justify-between gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10 text-primary">
                  <Upload className="h-5 w-5" />
                </div>
                <div className="text-right">
                  <div className="font-semibold">
                    {lang === 'ar' ? 'رفع بيانات جغرافية (GeoJSON)' : 'Upload Geo Data (GeoJSON)'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {lang === 'ar'
                      ? 'إضافة أو تحديث الدول والولايات والمدن والأحياء عبر ملفات GeoJSON'
                      : 'Add or update countries, states, cities, and neighborhoods from GeoJSON files'}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm">{lang === 'ar' ? 'فتح' : 'Open'}</Button>
            </Link>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="general" className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <TabsList className="inline-flex w-auto min-w-full md:grid md:w-full grid-cols-3 lg:grid-cols-6 gap-1">
              <TabsTrigger value="general" className="gap-1 md:gap-2 flex-shrink-0">
                <SettingsIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{tp.settings.tabGeneral}</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="gap-1 md:gap-2 flex-shrink-0">
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">{tp.settings.tabNotifications}</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="gap-1 md:gap-2 flex-shrink-0">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">{tp.settings.tabSecurity}</span>
              </TabsTrigger>
              <TabsTrigger value="appearance" className="gap-1 md:gap-2 flex-shrink-0">
                <Palette className="h-4 w-4" />
                <span className="hidden sm:inline">{tp.settings.tabAppearance}</span>
              </TabsTrigger>
              <TabsTrigger value="integration" className="gap-1 md:gap-2 flex-shrink-0">
                <Webhook className="h-4 w-4" />
                <span className="hidden sm:inline">{tp.settings.tabIntegration}</span>
              </TabsTrigger>
              <TabsTrigger value="database" className="gap-1 md:gap-2 flex-shrink-0">
                <Database className="h-4 w-4" />
                <span className="hidden sm:inline">{tp.settings.tabDatabase}</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">{tp.settings.generalTitle}</h3>
              <div className="space-y-4">
                <div className="space-y-2 text-right">
                  <Label htmlFor="company-name">{tp.settings.companyNameLabel}</Label>
                  <Input id="company-name" placeholder={tp.settings.companyNamePlaceholder} defaultValue="شركة التوصيل السريع" className="text-right" />
                </div>
                <div className="space-y-2 text-right">
                  <Label htmlFor="company-email">{tp.settings.emailLabel}</Label>
                  <Input id="company-email" type="email" placeholder="info@company.com" defaultValue="info@delivery.sa" className="text-right" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 text-right">
                    <Label htmlFor="phone">{tp.settings.phoneLabel}</Label>
                    <Input id="phone" placeholder={tp.settings.phonePlaceholder} defaultValue="0501234567" className="text-right" />
                  </div>
                  <div className="space-y-2 text-right">
                    <Label htmlFor="timezone">{tp.settings.timezoneLabel}</Label>
                    <Select defaultValue="riyadh">
                      <SelectTrigger id="timezone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="riyadh">الرياض (GMT+3)</SelectItem>
                        <SelectItem value="jeddah">جدة (GMT+3)</SelectItem>
                        <SelectItem value="dubai">دبي (GMT+4)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2 text-right">
                  <Label htmlFor="address">{tp.settings.addressLabel}</Label>
                  <Textarea id="address" placeholder={tp.settings.addressPlaceholder} defaultValue="الرياض، المملكة العربية السعودية" className="text-right" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5 text-right flex-1">
                    <Label>{tp.settings.maintenanceModeLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.maintenanceModeDescription}</p>
                  </div>
                  <Switch />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5 text-right flex-1">
                    <Label>{tp.settings.allowRegistrationLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.allowRegistrationDescription}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
            </Card>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 ml-2" />
                {tp.settings.saveChangesButton}
              </Button>
            </div>
          </TabsContent>

          {/* Notifications */}
          <TabsContent value="notifications" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{tp.settings.emailNotificationsTitle}</h3>
              </div>
              <div className="space-y-4">
                {[
                  { label: tp.settings.newShipmentLabel, desc: tp.settings.newShipmentDescription, checked: true },
                  { label: tp.settings.shipmentStatusLabel, desc: tp.settings.shipmentStatusDescription, checked: true },
                  { label: tp.settings.lateShipmentLabel, desc: tp.settings.lateShipmentDescription, checked: true },
                  { label: tp.settings.dailyReportsLabel, desc: tp.settings.dailyReportsDescription, checked: false },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between">
                    <div className="space-y-0.5 text-right flex-1">
                      <Label>{item.label}</Label>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch defaultChecked={item.checked} />
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Smartphone className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{tp.settings.smsNotificationsTitle}</h3>
              </div>
              <div className="space-y-4">
                {[
                  { label: tp.settings.deliveryConfirmationLabel, desc: tp.settings.deliveryConfirmationDescription },
                  { label: tp.settings.completionConfirmationLabel, desc: tp.settings.completionConfirmationDescription },
                  { label: tp.settings.verificationCodeLabel, desc: tp.settings.verificationCodeDescription },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{item.label}</Label>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                ))}
              </div>
            </Card>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 ml-2" />
                {tp.settings.saveChangesButton}
              </Button>
            </div>
          </TabsContent>

          {/* Security */}
          <TabsContent value="security" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">{tp.settings.securityTitle}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{tp.settings.twoFactorLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.twoFactorDescription}</p>
                  </div>
                  <Switch />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{tp.settings.strongPasswordLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.strongPasswordDescription}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="session-timeout">{tp.settings.sessionTimeoutLabel}</Label>
                  <Input id="session-timeout" type="number" defaultValue="30" />
                  <p className="text-sm text-muted-foreground">{tp.settings.sessionTimeoutDescription}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-attempts">{tp.settings.loginAttemptsLabel}</Label>
                  <Input id="max-attempts" type="number" defaultValue="5" />
                  <p className="text-sm text-muted-foreground">{tp.settings.loginAttemptsDescription}</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{tp.settings.activityLoggingLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.activityLoggingDescription}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{tp.settings.newLoginAlertLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.newLoginAlertDescription}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">{tp.settings.apiKeysTitle}</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="api-key">{tp.settings.primaryApiKeyLabel}</Label>
                  <div className="flex gap-2">
                    <Input id="api-key" type="password" value="••••••••••••••••" readOnly />
                    <Button variant="outline">{tp.settings.refreshButton}</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhook-secret">{tp.settings.webhookSecretLabel}</Label>
                  <div className="flex gap-2">
                    <Input id="webhook-secret" type="password" value="••••••••••••••••" readOnly />
                    <Button variant="outline">{tp.settings.refreshButton}</Button>
                  </div>
                </div>
              </div>
            </Card>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 ml-2" />
                {tp.settings.saveChangesButton}
              </Button>
            </div>
          </TabsContent>

          {/* Appearance */}
          <TabsContent value="appearance" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">{tp.settings.appearanceTitle}</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="theme">{tp.settings.themeLabel}</Label>
                  <Select value={theme} onValueChange={setTheme}>
                    <SelectTrigger id="theme"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">{tp.settings.themeLight}</SelectItem>
                      <SelectItem value="dark">{tp.settings.themeDark}</SelectItem>
                      <SelectItem value="system">{tp.settings.themeAuto}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">{tp.settings.languageLabel}</Label>
                  <Select defaultValue="ar">
                    <SelectTrigger id="language"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ar">{tp.settings.languageArabic}</SelectItem>
                      <SelectItem value="en">{tp.settings.languageEnglish}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="primary-color">{tp.settings.primaryColorLabel}</Label>
                  <div className="flex gap-2">
                    <Input id="primary-color" type="color" defaultValue="#0066FF" className="w-20 h-10" />
                    <Input defaultValue="#0066FF" readOnly />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo">{tp.settings.companyLogoLabel}</Label>
                  <div className="flex gap-2">
                    <Input id="logo" type="file" accept="image/*" />
                    <Button variant="outline">{tp.settings.uploadButton}</Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{tp.settings.collapsedSidebarLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.collapsedSidebarDescription}</p>
                  </div>
                  <Switch />
                </div>
              </div>
            </Card>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 ml-2" />
                {tp.settings.saveChangesButton}
              </Button>
            </div>
          </TabsContent>

          {/* Integration */}
          <TabsContent value="integration" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Webhook className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{tp.settings.webhooksTitle}</h3>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="webhook-url">{tp.settings.webhookUrlLabel}</Label>
                  <Input id="webhook-url" placeholder={tp.settings.webhookUrlPlaceholder} />
                  <p className="text-sm text-muted-foreground">{tp.settings.webhookUrlDescription}</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{tp.settings.enableWebhooksLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.enableWebhooksDescription}</p>
                  </div>
                  <Switch />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{tp.settings.emailServiceTitle}</h3>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp-host">{tp.settings.smtpHostLabel}</Label>
                  <Input id="smtp-host" placeholder={tp.settings.smtpHostPlaceholder} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-port">{tp.settings.smtpPortLabel}</Label>
                    <Input id="smtp-port" placeholder={tp.settings.smtpPortPlaceholder} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-encryption">{tp.settings.smtpEncryptionLabel}</Label>
                    <Select defaultValue="tls">
                      <SelectTrigger id="smtp-encryption"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{tp.settings.encryptionNone}</SelectItem>
                        <SelectItem value="tls">{tp.settings.encryptionTls}</SelectItem>
                        <SelectItem value="ssl">{tp.settings.encryptionSsl}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp-username">{tp.settings.usernameLabel}</Label>
                  <Input id="smtp-username" type="email" placeholder={tp.settings.usernamePlaceholder} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp-password">{tp.settings.passwordLabel}</Label>
                  <Input id="smtp-password" type="password" placeholder="••••••••" />
                </div>
                <Button variant="outline">{tp.settings.testConnectionButton}</Button>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Smartphone className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{tp.settings.smsServiceTitle}</h3>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sms-provider">{tp.settings.smsProviderLabel}</Label>
                  <Select defaultValue="twilio">
                    <SelectTrigger id="sms-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="twilio">{tp.settings.providerTwilio}</SelectItem>
                      <SelectItem value="vonage">{tp.settings.providerVonage}</SelectItem>
                      <SelectItem value="custom">{tp.settings.providerCustom}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sms-api-key">{tp.settings.apiKeyLabel}</Label>
                  <Input id="sms-api-key" type="password" placeholder="••••••••••••••••" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sms-sender">{tp.settings.senderNameLabel}</Label>
                  <Input id="sms-sender" placeholder="DELIVERY" />
                </div>
                <Button variant="outline">{tp.settings.testConnectionButton}</Button>
              </div>
            </Card>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 ml-2" />
                {tp.settings.saveChangesButton}
              </Button>
            </div>
          </TabsContent>

          {/* Database */}
          <TabsContent value="database" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">{tp.settings.backupTitle}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{tp.settings.autoBackupLabel}</Label>
                    <p className="text-sm text-muted-foreground">{tp.settings.autoBackupDescription}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="backup-time">{tp.settings.backupTimeLabel}</Label>
                  <Input id="backup-time" type="time" defaultValue="02:00" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="backup-retention">{tp.settings.backupRetentionLabel}</Label>
                  <Input id="backup-retention" type="number" defaultValue="30" />
                </div>
                <div className="flex gap-2">
                  <Button>{tp.settings.createBackupButton}</Button>
                  <Button variant="outline">{tp.settings.restoreBackupButton}</Button>
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-destructive">{tp.settings.dangerZoneTitle}</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{tp.settings.deleteOldDataLabel}</Label>
                  <p className="text-sm text-muted-foreground mb-2">{tp.settings.deleteOldDataDescription}</p>
                  <Select defaultValue="never">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">{tp.settings.deleteNever}</SelectItem>
                      <SelectItem value="6months">{tp.settings.delete6Months}</SelectItem>
                      <SelectItem value="1year">{tp.settings.delete1Year}</SelectItem>
                      <SelectItem value="2years">{tp.settings.delete2Years}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="pt-4 border-t">
                  <Button variant="destructive">{tp.settings.resetSystemButton}</Button>
                  <p className="text-sm text-muted-foreground mt-2">{tp.settings.resetSystemWarning}</p>
                </div>
              </div>
            </Card>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={loading}>
                <Save className="h-4 w-4 ml-2" />
                {tp.settings.saveChangesButton}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
}
